import { Injectable } from '@angular/core';
import { GitQuery } from '@y42/git';
import { OrgQuery } from '@y42/org/state';
import { OrgSpaceQuery } from '@y42/org-space/state';
import { PusherEvent, PushNotificationService } from '@y42/push-notification';
import { catchError, combineLatest, filter, Observable, of, shareReplay, switchMap } from 'rxjs';
import { OrchestrationRunHttpModel } from './orchestration-run-http.service';

export interface OrchestrationWsEvent {
  data: {
    orchestration_run: OrchestrationRunHttpModel;
  };
  org_id?: number;
  storage_branch?: string;
  storage_id?: string;
  storage_type?: string;
}

@Injectable({ providedIn: 'root' })
export class OrchestrationRunRealtimeService {
  constructor(
    private readonly pushNotificationService: PushNotificationService,
    private readonly orgQuery: OrgQuery,
    private readonly orgSpaceQuery: OrgSpaceQuery,
    private readonly gitQuery: GitQuery,
  ) {}

  private readonly pushNotifications$ = combineLatest([this.orgQuery.activeOrg$, this.orgSpaceQuery.active$]).pipe(
    filter((vals) => vals.every((val) => val)),
    switchMap(() =>
      this.pushNotificationService.openChannel<OrchestrationWsEvent>(this.getChannelName()).pipe(
        catchError((err: unknown) => {
          console.error(err);
          return of(null);
        }),
      ),
    ),
    filter(Boolean),
    filter((event) => {
      const activeBranchName = this.gitQuery.getActiveBranchName();
      const activeSpace = this.orgSpaceQuery.getActiveId();
      const activeOrgId = `${this.orgQuery.getActiveId()}`;
      const model = event.data;

      return (
        !!activeBranchName &&
        !!activeSpace &&
        !!activeOrgId &&
        model.storage_branch === activeBranchName &&
        model.storage_id === activeSpace &&
        `${model.org_id}` === `${activeOrgId}`
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  listen<T extends OrchestrationWsEvent>(
    predicate: (pushNotification: PusherEvent<T>) => boolean = () => true,
  ): Observable<PusherEvent<T>> {
    return (this.pushNotifications$ as Observable<PusherEvent<T>>).pipe(filter(predicate));
  }

  private getChannelName(): string {
    return `orchestration.${this.orgQuery.getActiveId()}.${this.orgSpaceQuery.getActiveId()}`;
  }
}
